// Adapted to React from this article
// https://css-tricks.com/how-to-animate-the-details-element-using-waapi/

import React, { useContext, useEffect, useRef, useState } from 'react';
import Icon from '../icon/icon';
import AccordionContext from './accordion.context';

export interface AccordionProps {
    title: string;
    internalSectionComponentId?: string;
    classes?: {
        details?: string,
        summary?: string,
        summaryText?: string,
        icon?: string
    }
    children?: React.ReactNode;
}

const Accordion = ({ title, classes, internalSectionComponentId, children }: React.PropsWithChildren<AccordionProps>): React.ReactElement => {

    const details = useRef<HTMLDetailsElement>(null);
    const summary = useRef<HTMLElement>(null);
    const content = useRef<HTMLDivElement>(null);

    const [animation, setAnimation] = useState<Animation | null>(null);
    const [accordionOpen, setAccordionOpen] = useState(false);
    const [closing, setClosing] = useState(false);
    const [expanding, setExpanding] = useState(false);

    const { activeGroup, setActiveGroup } = useContext(AccordionContext);

    useEffect(() => {
        if (accordionOpen && setActiveGroup && activeGroup !== title) {
            shrink();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeGroup, accordionOpen, title]);

    const handleClick = (e: React.MouseEvent<HTMLElement, MouseEvent>): void => {
        if (details.current) {
            // Stop default behaviour from the browser
            e.preventDefault();

            // Check if the element is being closed or is already closed
            if (closing || !accordionOpen) {
                openAccordion();
                setActiveGroup && setActiveGroup(title);
                // Check if the element is being openned or is already open
            } else if (expanding || accordionOpen) {
                shrink();
            }
        }
    };

    const openAccordion = (): void => {
        if (details.current) {
            // Apply a fixed height on the element
            details.current.style.height = `${details.current.offsetHeight}px`;

            // Force the [open] attribute on the details element
            setAccordionOpen(true);

            // Wait for the next frame to call the expand function
            window.requestAnimationFrame(() => expand());
        }
    };

    const expand = (): void => {
        if (details.current && summary.current && content.current) {
            // Set the element as "being expanding"
            setExpanding(true);

            // Get the current fixed height of the element
            const startHeight = `${details.current.offsetHeight}px`;
            // Calculate the open height of the element (summary height + content height)
            const endHeight = `${summary.current.offsetHeight + content.current.offsetHeight}px`;

            // If there is already an animation running
            if (animation) {
                // Cancel the current animation
                animation.cancel();
            }

            // Start a WAAPI animation
            const expandAnimation = details.current.animate({
                // Set the keyframes from the startHeight to endHeight
                height: [startHeight, endHeight]
            }, {
                // If the duration is too slow of fast, you can change it here
                duration: 400,
                // You can also change the ease of the animation
                easing: 'ease-out'
            });

            // When the animation is complete, call onAnimationFinish()
            expandAnimation.onfinish = () => onAnimationFinish(true);

            // If the animation is cancelled, isExpanding variable is set to false
            expandAnimation.oncancel = () => setExpanding(false);

            setAnimation(expandAnimation);
        }
    };

    const shrink = (): void => {
        if (details.current && summary.current) {
            // Set the element as "being closed"
            setClosing(true);

            // Store the current height of the element
            const startHeight = `${details.current.offsetHeight}px`;

            // Calculate the height of the summary
            const endHeight = `${summary.current.offsetHeight}px`;

            // If there is already an animation running
            if (animation) {
                // Cancel the current animation
                animation.cancel();
            }

            // Start a WAAPI animation
            const shrinkAnimation = details.current.animate({
                // Set the keyframes from the startHeight to endHeight
                height: [startHeight, endHeight]
            }, {
                // If the duration is too slow or fast, you can change it here
                duration: 400,
                // You can also change the ease of the animation
                easing: 'ease-out'
            });

            // When the animation is complete, call onAnimationFinish()
            shrinkAnimation.onfinish = () => onAnimationFinish(false);

            // If the animation is cancelled, isClosing variable is set to false
            shrinkAnimation.oncancel = () => setClosing(false);

            setAnimation(shrinkAnimation);
        }
    };

    const onAnimationFinish = (open: boolean): void => {
        if (details.current) {
            // Set the open attribute based on the parameter
            setAccordionOpen(open);

            // Clear the stored animation
            setAnimation(null);

            // Reset isClosing & isExpanding
            setClosing(false);
            setExpanding(false);

            // Remove the fixed height
            details.current.style.height = '';
        }
    };

    return (
        <details ref={details} id={internalSectionComponentId} open={accordionOpen} className={`overflow-hidden ${classes?.details || ''}`}>
            <summary ref={summary} onClick={(e) => handleClick(e)} className={`list-none flex items-center cursor-pointer ${ classes?.summary || '' }`} aria-expanded={accordionOpen}>
                <h3 className={classes?.summaryText || ''}>{title}</h3>
                <Icon name="expand_more" className={`${ (accordionOpen || expanding) && !closing ? 'rotate-180' : 'rotate-0' } transform transition-transform duration-300 w-12 h-12 ${ classes?.icon || '' }`} />
            </summary>
            <div className="content" ref={content}>
                {children}
            </div>
        </details>
    );
};

export default Accordion;
